import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ShieldCheck, Loader2 } from "lucide-react";
import { Wordmark } from "../components/landing/Wordmark";
import { fetchContent } from "../lib/api";
import { loadCaptcha, executeCaptcha } from "../lib/captcha";
import { getPublicQuestions, submitProfiler } from "../components/riskprofiler/riskApi";

const OPTS = ["A", "B", "C", "D"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls = "w-full bg-white border border-oth-line rounded-xl px-4 py-3 font-body text-base focus:outline-none focus:border-oth-blue transition-colors";

const profileTone = (p) => ({
    Conservative: "text-emerald-700", Moderate: "text-oth-blue", Aggressive: "text-[#b45309]",
}[p] || "text-oth-ink");

export default function RiskProfiler() {
    const [brand, setBrand] = useState(null);
    const [captchaOn, setCaptchaOn] = useState(false);
    const [questions, setQuestions] = useState(null);
    const [disclaimer, setDisclaimer] = useState("");
    const [step, setStep] = useState(0); // 0 = basic info, 1..N = questions
    const [info, setInfo] = useState({ name: "", email: "", age: "" });
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        getPublicQuestions().then((d) => { setQuestions(d.questions); setDisclaimer(d.disclaimer); })
            .catch(() => toast.error("Could not load the profiler. Please try again."));
        fetchContent().then((c) => {
            setBrand(c?.brand || null);
            if (c?.integrations?.recaptcha_enabled && c?.integrations?.recaptcha_site_key) {
                setCaptchaOn(true);
                loadCaptcha(c.integrations.recaptcha_site_key);
            }
        }).catch(() => {});
    }, []);

    const total = questions?.length || 0;
    const progress = useMemo(() => (step === 0 ? 0 : Math.round((step / total) * 100)), [step, total]);

    const validInfo = () => {
        if (info.name.trim().length < 2) { toast.error("Please enter your name."); return false; }
        if (!EMAIL_RE.test(info.email.trim())) { toast.error("Please enter a valid email address."); return false; }
        const age = Number(info.age);
        if (!age || age < 18 || age > 100) { toast.error("Age must be between 18 and 100."); return false; }
        return true;
    };

    const next = () => {
        if (step === 0) { if (!validInfo()) return; setStep(1); window.scrollTo({ top: 0 }); return; }
        const q = questions[step - 1];
        if (!answers[q.id]) { toast.error("Please choose an option to continue."); return; }
        if (step < total) { setStep(step + 1); window.scrollTo({ top: 0 }); }
    };
    const back = () => { if (step > 0) { setStep(step - 1); window.scrollTo({ top: 0 }); } };

    const submit = async () => {
        const q = questions[step - 1];
        if (!answers[q.id]) { toast.error("Please choose an option to continue."); return; }
        const missing = questions.find((x) => !answers[x.id]);
        if (missing) { toast.error("Please answer every question."); return; }
        setSubmitting(true);
        try {
            let captcha_token = "";
            if (captchaOn) captcha_token = await executeCaptcha("riskprofiler");
            const res = await submitProfiler({
                name: info.name.trim(), email: info.email.trim(), age: Number(info.age),
                answers, captcha_token,
            });
            setResult(res);
            window.scrollTo({ top: 0 });
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not submit. Please check your answers and try again.");
        } finally { setSubmitting(false); }
    };

    const Header = () => (
        <header className="sticky top-0 z-30 bg-[rgba(245,245,242,0.9)] backdrop-blur-md border-b border-oth-line/70">
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                <Link to="/" data-testid="rp-home-link">
                    <Wordmark logoUrl={brand?.logo_url || ""} brandName={brand?.name || "One Tree Hill"} wordmark={brand?.wordmark || "Prime Wealth"} logoSize={brand?.logo_size || "md"} nameColor={brand?.name_color || ""} nameFont={brand?.name_font || ""} nameSize={brand?.name_size || 0} />
                </Link>
                <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body hidden sm:block">Risk Profiler</div>
            </div>
        </header>
    );

    if (!questions) {
        return <div className="min-h-screen flex items-center justify-center font-editorial italic text-oth-ink3" data-testid="rp-loading">Loading profiler…</div>;
    }

    // ---- result screen
    if (result) {
        return (
            <div className="min-h-screen" data-testid="rp-result">
                <div className="noise-layer" aria-hidden />
                <Header />
                <main className="max-w-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
                    <div className="rounded-3xl border border-oth-line/70 bg-white p-7 md:p-10 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-[#eef3f8] text-oth-blue flex items-center justify-center mx-auto mb-5"><Check size={26} /></div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-oth-ink3 font-body mb-2">Your Risk Profile</div>
                        <h1 className={`font-display text-4xl md:text-5xl font-semibold tracking-tight ${profileTone(result.profile)}`} data-testid="rp-result-profile">{result.profile}</h1>
                        <div className="mt-4 flex items-center justify-center gap-6 text-sm font-body text-oth-ink2">
                            <div><span className="font-display text-2xl font-semibold text-oth-ink block" data-testid="rp-result-score">{result.total_score}</span><span className="text-oth-ink3">of {result.max_score}</span></div>
                            <div className="w-px h-10 bg-oth-line" />
                            <div><span className="font-display text-2xl font-semibold text-oth-ink block">{result.question_count}</span><span className="text-oth-ink3">questions</span></div>
                        </div>
                        <p className="mt-6 text-base text-oth-ink2 font-body leading-relaxed" data-testid="rp-result-description">{result.description}</p>
                    </div>
                    <div className="mt-6 rounded-2xl border border-oth-line/60 bg-[#f0f0ec] px-5 py-4 text-[12px] leading-relaxed font-body text-oth-ink3" data-testid="rp-result-disclaimer">
                        {result.disclaimer}
                    </div>
                    <div className="mt-8 text-center">
                        <Link to="/" className="btn-pill btn-pill-dark text-sm" data-testid="rp-result-home">Back to home</Link>
                    </div>
                </main>
            </div>
        );
    }

    const onLast = step === total;

    return (
        <div className="min-h-screen" data-testid="risk-profiler-page">
            <div className="noise-layer" aria-hidden />
            <Header />
            <main className="max-w-2xl mx-auto px-4 md:px-8 py-8 md:py-12">
                {step === 0 ? (
                    <div data-testid="rp-step-info">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-oth-blue font-body mb-2">Risk Profiler</div>
                        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Understand your investment risk profile.</h1>
                        <p className="text-sm md:text-base text-oth-ink3 font-body mt-2">Answer {total} simple questions. It takes about two minutes — no login required.</p>
                        <div className="mt-8 rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8 space-y-5">
                            <label className="block">
                                <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Name</div>
                                <input className={inputCls} value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} placeholder="Your full name" data-testid="rp-name" />
                            </label>
                            <label className="block">
                                <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Email</div>
                                <input type="email" className={inputCls} value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} placeholder="you@example.com" data-testid="rp-email" />
                            </label>
                            <label className="block">
                                <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Age</div>
                                <input type="number" min="18" max="100" className={inputCls} value={info.age} onChange={(e) => setInfo({ ...info, age: e.target.value })} placeholder="e.g. 42" data-testid="rp-age" />
                            </label>
                        </div>
                    </div>
                ) : (
                    <div data-testid={`rp-step-${step}`}>
                        <div className="mb-6">
                            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-2">
                                <span data-testid="rp-progress-label">Question {step} of {total}</span>
                                <span>{progress}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-oth-line/60 overflow-hidden">
                                <div className="h-full bg-oth-blue transition-all duration-500" style={{ width: `${progress}%` }} />
                            </div>
                        </div>
                        <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-snug mb-6" data-testid="rp-question-text">
                            {questions[step - 1].question_text}
                        </h2>
                        <div className="space-y-3">
                            {OPTS.map((letter) => {
                                const q = questions[step - 1];
                                const text = q[`option_${letter.toLowerCase()}`];
                                const selected = answers[q.id] === letter;
                                return (
                                    <button
                                        key={letter}
                                        onClick={() => setAnswers({ ...answers, [q.id]: letter })}
                                        className={`w-full text-left flex items-start gap-4 rounded-2xl border px-5 py-4 transition-all ${selected ? "border-oth-blue bg-[#eef3f8] shadow-sm" : "border-oth-line bg-white hover:border-oth-blue/60"}`}
                                        data-testid={`rp-option-${letter}`}
                                    >
                                        <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-display font-semibold ${selected ? "bg-oth-blue text-white" : "bg-[#f0f0ec] text-oth-ink2"}`}>{letter}</span>
                                        <span className="font-body text-[15px] text-oth-ink leading-snug pt-1">{text}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between mt-9">
                    <button onClick={back} disabled={step === 0} className="btn-pill btn-pill-light text-sm disabled:opacity-40" data-testid="rp-back"><ArrowLeft size={14} /> Back</button>
                    {onLast ? (
                        <button onClick={submit} disabled={submitting} className="btn-pill btn-pill-blue text-sm disabled:opacity-60" data-testid="rp-submit">
                            {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <>See my result <ArrowRight size={14} /></>}
                        </button>
                    ) : (
                        <button onClick={next} className="btn-pill btn-pill-dark text-sm" data-testid="rp-next">{step === 0 ? "Start" : "Next"} <ArrowRight size={14} /></button>
                    )}
                </div>

                {step === 0 && (
                    <div className="mt-8 flex items-start gap-2.5 text-[12px] leading-relaxed font-body text-oth-ink3" data-testid="rp-privacy-note">
                        <ShieldCheck size={15} className="shrink-0 mt-0.5 text-oth-blue" />
                        {disclaimer}
                    </div>
                )}
            </main>
        </div>
    );
}
